<template>
  <main class="app-main contracts-page">
    <div class="app-content-header">
      <div class="container-fluid">
        <ol class="breadcrumb mb-3">
          <li class="breadcrumb-item"><router-link to="/">الرئيسية</router-link></li>
          <li v-if="canRead" class="breadcrumb-item">
            <router-link to="/contracts">منصة العقود العقارية</router-link>
          </li>
          <li class="breadcrumb-item active">مكتبة نماذج العقود</li>
        </ol>
        <div class="contract-heading">
          <div>
            <h1>
              <i class="bi bi-folder2-open ms-2" aria-hidden="true"></i>
              مكتبة نماذج العقود
            </h1>
            <p>نماذج العقود المعتمدة والأنواع المخصصة لفريق العمل</p>
          </div>
          <div class="contract-actions">
            <router-link v-if="canRead" to="/contracts" class="btn btn-outline-primary">سجل العقود</router-link>
            <router-link v-if="canCreate" to="/contracts/create" class="btn btn-outline-primary">إضافة عقد</router-link>
            <button v-if="canManage" type="button" class="btn btn-primary" :disabled="saving" @click="openEditor()">
              <i class="bi bi-plus-lg ms-1" aria-hidden="true"></i>
              إضافة نموذج مخصص
            </button>
          </div>
        </div>
      </div>
    </div>
    <div class="app-content">
      <div class="container-fluid">
        <div v-if="!canView" class="contract-card contract-access-denied">ليس لديك صلاحية عرض نماذج العقود.</div>
        <template v-else>
          <errorAlert v-if="hasError" :alertMessage="message" :errorNumber="errorNumber" />
          <section
            v-if="editorOpen && canManage"
            ref="editor"
            class="contract-card mb-4"
            aria-labelledby="template-editor-title"
          >
            <div class="contract-card-header">
              <h2 id="template-editor-title">
                {{ editingType ? `تحديث نموذج: ${editingType.name}` : 'إضافة نوع عقد ونموذج مخصص' }}
              </h2>
              <button type="button" class="btn btn-outline-secondary btn-sm" :disabled="saving" @click="closeEditor">
                إغلاق
              </button>
            </div>
            <form @submit.prevent="saveTemplate">
              <fieldset :disabled="saving" class="border-0 p-0 m-0">
                <div class="contract-card-body">
                  <div v-if="editorError" class="alert alert-danger" role="alert">{{ editorError }}</div>
                  <div class="row g-3">
                    <div class="col-12 col-md-6">
                      <label for="template-name" class="form-label">
                        اسم نوع العقد / النموذج
                        <span class="text-danger">*</span>
                      </label>
                      <input
                        id="template-name"
                        ref="nameInput"
                        v-model.trim="draft.name"
                        type="text"
                        class="form-control"
                        maxlength="255"
                        required
                        placeholder="اسم النموذج"
                      />
                    </div>
                    <div class="col-12 col-md-6">
                      <label for="template-file" class="form-label">
                        ملف النموذج
                        <span v-if="!editingType?.template" class="text-danger">*</span>
                      </label>
                      <input
                        id="template-file"
                        :key="fileKey"
                        type="file"
                        class="form-control"
                        accept=".pdf,.doc,.docx"
                        :required="!editingType?.template"
                        @change="fileChanged"
                      />
                      <p class="contract-hint mt-2 mb-0">PDF أو Word (.doc / .docx) · الحد الأقصى 15 MB</p>
                    </div>
                    <div class="col-12">
                      <label for="template-description" class="form-label">وصف النموذج</label>
                      <textarea
                        id="template-description"
                        v-model.trim="draft.description"
                        class="form-control"
                        rows="3"
                        maxlength="2000"
                        placeholder="وصف مختصر يوضح استخدام النموذج"
                      ></textarea>
                    </div>
                  </div>
                  <div v-if="file" class="contract-template-state mt-3">
                    <i class="bi bi-file-earmark-arrow-up ms-1" aria-hidden="true"></i>
                    الملف الجديد:
                    <strong class="contract-file-name">{{ file.name }}</strong>
                    · {{ fileSize(file.size) }}
                  </div>
                  <p v-else-if="editingType?.template" class="contract-hint mt-3 mb-0">
                    الملف الحالي: {{ editingType.template.name }}. ارفع ملفاً جديداً لاستبداله.
                  </p>
                  <p v-if="!editingType" class="contract-hint mt-3 mb-0">
                    سيظهر النوع الجديد ضمن خيارات إضافة العقود بعد حفظ النموذج.
                  </p>
                </div>
                <div class="contract-card-footer justify-content-end">
                  <button type="button" class="btn btn-outline-secondary" @click="closeEditor">إلغاء</button>
                  <button type="submit" class="btn btn-primary">
                    <span v-if="saving" class="spinner-border spinner-border-sm ms-1" aria-hidden="true"></span>
                    {{ saving ? 'جارٍ حفظ النموذج...' : 'حفظ النموذج' }}
                  </button>
                </div>
              </fieldset>
            </form>
          </section>
          <div v-if="loading" class="py-4"><mainLoader /></div>
          <div v-else-if="hasError" class="contract-card contract-empty">
            <i class="bi bi-exclamation-circle" aria-hidden="true"></i>
            <h3>تعذر تحميل مكتبة النماذج</h3>
            <button type="button" class="btn btn-outline-primary" @click="load">إعادة المحاولة</button>
          </div>
          <div v-else-if="!types.length" class="contract-card contract-empty">
            <i class="bi bi-folder2" aria-hidden="true"></i>
            <h3>لا توجد نماذج عقود</h3>
            <p>يمكن للموظف المخوّل إضافة نوع عقد ورفع نموذجه.</p>
            <button v-if="canManage" type="button" class="btn btn-primary" @click="openEditor()">إضافة نموذج</button>
          </div>
          <div v-else class="contract-template-grid mb-4">
            <article v-for="type in types" :key="type.id" class="contract-card contract-template">
              <div class="contract-card-body">
                <div class="contract-template-icon">
                  <i
                    class="bi"
                    :class="type.template ? 'bi-file-earmark-text' : 'bi-file-earmark-plus'"
                    aria-hidden="true"
                  ></i>
                </div>
                <h2>{{ type.name }}</h2>
                <p>{{ type.description || 'نموذج مرتبط بهذا النوع من العقود.' }}</p>
                <div v-if="type.template" class="contract-template-state">
                  <div class="contract-file-name fw-bold">{{ type.template.name }}</div>
                  <span class="contract-hint">{{ fileSize(type.template.size) }}</span>
                </div>
                <div v-else class="contract-template-state">
                  <i class="bi bi-info-circle ms-1" aria-hidden="true"></i>
                  لم يُرفع ملف نموذج لهذا النوع بعد.
                </div>
              </div>
              <div class="contract-card-footer">
                <button
                  v-if="type.template"
                  type="button"
                  class="btn btn-outline-primary btn-sm"
                  :disabled="downloadingId === type.id"
                  @click="download(type)"
                >
                  <i class="bi bi-download ms-1" aria-hidden="true"></i>
                  {{ downloadingId === type.id ? 'جارٍ التنزيل...' : 'تنزيل النموذج' }}
                </button>
                <span v-else class="contract-hint">النموذج غير متاح للتنزيل</span>
                <button
                  v-if="canManage"
                  type="button"
                  class="btn btn-primary btn-sm"
                  :disabled="saving"
                  @click="openEditor(type)"
                >
                  {{ type.template ? 'تحديث النموذج' : 'رفع النموذج' }}
                </button>
              </div>
            </article>
          </div>
        </template>
      </div>
    </div>
  </main>
</template>

<script>
import { checkAccess, toast } from '@/helpers/tools';
import { fileSize, validateFile, authenticatedDownload, errorMessage } from './helpers';
import './contracts.css';

export default {
  name: 'ContractTemplates',
  data() {
    return {
      types: [],
      loading: true,
      hasError: false,
      message: '',
      errorNumber: 0,
      editorOpen: false,
      editingType: null,
      draft: { name: '', description: '' },
      file: null,
      fileKey: 0,
      editorError: '',
      saving: false,
      downloadingId: null,
      serial: 0,
    };
  },
  computed: {
    canRead() {
      return checkAccess('contracts_read');
    },
    canCreate() {
      return checkAccess('contracts_create');
    },
    canManage() {
      return checkAccess('contract_templates_manage');
    },
    canView() {
      return this.canRead || this.canCreate || this.canManage || checkAccess('contracts_update');
    },
  },
  methods: {
    fileSize,
    async load() {
      if (!this.canView) {
        this.loading = false;
        return;
      }
      const serial = ++this.serial;
      this.loading = true;
      this.hasError = false;
      try {
        const { data } = await this.$http.get('/api/admin/contracts/templates');
        if (serial === this.serial) this.types = Array.isArray(data.types) ? data.types : [];
      } catch (error) {
        if (serial === this.serial) {
          [this.message, this.errorNumber] = this.$globalError(error, true);
          this.hasError = true;
        }
      } finally {
        if (serial === this.serial) this.loading = false;
      }
    },
    async openEditor(type = null) {
      if (!this.canManage || this.saving) return;
      this.editingType = type;
      this.draft = { name: type?.name || '', description: type?.description || '' };
      this.file = null;
      this.fileKey += 1;
      this.editorError = '';
      this.editorOpen = true;
      await this.$nextTick();
      this.$refs.editor?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      this.$refs.nameInput?.focus({ preventScroll: true });
    },
    closeEditor() {
      if (this.saving) return;
      this.editorOpen = false;
      this.editingType = null;
      this.file = null;
      this.editorError = '';
    },
    fileChanged(event) {
      const file = event.target.files?.[0];
      const validation = validateFile(file, true);
      if (validation) {
        this.editorError = validation;
        event.target.value = '';
        this.file = null;
        return;
      }
      this.editorError = '';
      this.file = file || null;
    },
    async saveTemplate() {
      if (!this.canManage || this.saving) return;
      if (!this.draft.name.trim()) {
        this.editorError = 'أدخل اسم النموذج';
        return;
      }
      if (!this.file && !this.editingType?.template) {
        this.editorError = 'ارفع ملف النموذج أولاً';
        return;
      }
      this.saving = true;
      this.editorError = '';
      const body = new FormData();
      body.append('name', this.draft.name.trim());
      body.append('description', this.draft.description.trim());
      if (this.file) body.append('file', this.file);
      try {
        if (this.editingType) await this.$http.put(`/api/admin/contracts/templates/${this.editingType.id}`, body);
        else await this.$http.post('/api/admin/contracts/templates', body);
        toast('تم حفظ نموذج العقد', 'success');
        this.editorOpen = false;
        this.file = null;
        await this.load();
      } catch (error) {
        this.editorError = this.$globalError(error);
      } finally {
        this.saving = false;
      }
    },
    async download(type) {
      if (!type.template || this.downloadingId !== null) return;
      this.downloadingId = type.id;
      try {
        await authenticatedDownload(
          this.$http,
          `/api/admin/contracts/templates/${type.id}/download`,
          type.template.name
        );
      } catch (error) {
        toast(await errorMessage(this, error));
      } finally {
        this.downloadingId = null;
      }
    },
  },
  created() {
    this.load();
  },
  beforeUnmount() {
    this.serial += 1;
  },
};
</script>
